import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Package, MessageCircle, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cartStorage } from '../utils/cart';
import { useAuth } from '../contexts/AuthContext';
import { useGeo } from '../contexts/GeoContext';
import { useTranslation } from '../utils/translations';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { isBrazil } = useGeo();
  const tr = useTranslation(isBrazil);
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(cartStorage.getCart().length);
    };
    
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    
    const interval = setInterval(updateCartCount, 500);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      clearInterval(interval);
    };
  }, []);

  const navLinks = [
    { to: '/', label: tr('home') },
    { to: '/produtos', label: tr('products') },
    { to: '/feedbacks', label: tr('feedbacks') },
    ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin' }] : []),
    ...(!isAuthenticated ? [{ to: '/login', label: tr('login') }] : []),
  ];

  return (
    <header className="bg-black border-b border-gray-800 sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_07e3fa1c-accb-40af-b9f6-b18f873b4bec/artifacts/3s7e8l1h_logodas.png" 
                alt="Cloud Cheats Logo" 
                className="h-12 w-12 object-contain transition-transform group-hover:scale-110"
              />
            </div>
            <div className="hidden sm:block">
              <span className="text-white text-xl font-black tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>CLOUD</span>
              <span className="text-red-600 text-xl ml-1 font-black tracking-wider" style={{ fontFamily: 'Orbitron, sans-serif' }}>CHEATS</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}>
                <Button
                  variant="ghost"
                  className={`text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold transition-all ${
                    location.pathname === link.to ? 'bg-red-600/30 text-white border-b-2 border-red-600' : ''
                  }`}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-2">
            {isAuthenticated && (
              <>
                <Link to="/pedidos" className="hidden md:block">
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
                  >
                    <Package className="w-5 h-5 mr-2" />
                    {tr('orders')}
                  </Button>
                </Link>
                <Link to="/suporte" className="hidden md:block">
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {tr('support')}
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  onClick={logout}
                  className="hidden md:flex items-center text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  {tr('logout')}
                </Button>
              </>
            )}
            
            <Link to="/carrinho">
              <Button 
                variant="ghost" 
                className="relative text-gray-300 hover:text-white hover:bg-red-600/20 transition-all"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-red-600 text-white h-5 w-5 flex items-center justify-center p-0 text-xs pulse-badge">
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              className="md:hidden text-gray-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-800 bg-black">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold ${
                      location.pathname === link.to ? 'bg-red-600/30 text-white' : ''
                    }`}
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
              
              {/* Authenticated user mobile links */}
              {isAuthenticated && (
                <>
                  <Link to="/pedidos" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold ${
                        location.pathname === '/pedidos' ? 'bg-red-600/30 text-white' : ''
                      }`}
                    >
                      <Package className="w-5 h-5 mr-2" />
                      Pedidos
                    </Button>
                  </Link>
                  <Link to="/suporte" onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold ${
                        location.pathname === '/suporte' ? 'bg-red-600/30 text-white' : ''
                      }`}
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Suporte
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    onClick={() => {
                      setMobileMenuOpen(false);
                      logout();
                    }}
                    className="w-full justify-start text-gray-300 hover:text-white hover:bg-red-600/20 font-semibold"
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    Sair
                  </Button>
                </>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;